import { useEffect, useEffectEvent, useState, type CSSProperties, type ReactNode } from 'react';
import type { HouseRuleSpec } from '../../../engine';
import { sounds } from '../../../app/sound';
import { t, type MessageKey } from '../../../i18n';
import { itemName } from '../../../app/describe';
import { getScene, patchScene } from '../../scene';
import { useTheme } from '../../theme';
import { Emblem } from './Emblem';
import type { RuleClock } from './ruleClock';
import {
  DROP,
  EYE,
  EYE_SMALL,
  HOOK,
  HOOK_DRIP,
  SKELETON,
  SKELETON_FINGER,
  TENTACLE,
} from './pixelBroker';
import { SpritePaths } from './Sprite';

/**
 * The Broker (docs/LONG_NIGHT_ART_DIRECTION.md §12.11, §8.4–§8.5): hands, eyes and a voice in
 * text, never a face or a body. His hands are pixel sprites (scripts/art/pixel-broker.py) out of a
 * dark sleeve and a torn cuff: a skeleton's on the left, a hook dripping blood on the right, and
 * now and then a tentacle. They come in from the top edge, his side, and move only on Glide: a
 * slow curve that never overshoots. His eyes open at Closing Time and nowhere else. His lines
 * write themselves in the upper third, in italic, with no box.
 */

// ---- Hands -----------------------------------------------------------------------------------

export type HandKind = 'skeleton' | 'hook' | 'tentacle';

/** One of his hands, reaching down from the top edge: a pixel sprite. */
export function HandShape({ kind }: { kind: HandKind }) {
  const sprite = kind === 'skeleton' ? SKELETON : kind === 'hook' ? HOOK : TENTACLE[0]!;
  return (
    <svg
      className="bh-svg"
      viewBox={`0 0 ${sprite.w} ${sprite.h}`}
      shapeRendering="crispEdges"
      aria-hidden="true"
    >
      {kind === 'skeleton' && (
        <>
          <SpritePaths sprite={SKELETON} />
          {/* The middle finger, on its own so it can tap. */}
          <g className="bh-finger">
            <SpritePaths sprite={SKELETON_FINGER} />
          </g>
        </>
      )}
      {kind === 'hook' && (
        <>
          <SpritePaths sprite={HOOK} />
          {/* The blood gathers at the bend and falls, a drop at a time. */}
          <g transform={`translate(${HOOK_DRIP.x - 1} ${HOOK_DRIP.y - 1})`}>
            <g className="bh-drop">
              <SpritePaths sprite={DROP} />
            </g>
            <g className="bh-drop is-late">
              <SpritePaths sprite={DROP} />
            </g>
          </g>
        </>
      )}
      {kind === 'tentacle' &&
        TENTACLE.map((frame, i) => (
          <g key={i} className={`bh-wriggle is-${i}`}>
            <SpritePaths sprite={frame} />
          </g>
        ))}
    </svg>
  );
}

export type HandPose = 'away' | 'rest' | 'reach';

/**
 * A hand of his, from the top edge of its scene: the skeleton's on the left, the bloody hook on
 * the right. `rest`: lying on the far edge, now and then tapping a finger (every 10–16 s).
 * `reach`: down to `to` (a share of the scene's height), on Glide, holding whatever it brings or
 * takes. `away`: gone up into the smoke. Now and then, unannounced, a hand flickers and is a
 * tentacle for a few seconds, then flickers back (never with reduced motion).
 */
export function BrokerHand({
  side = 'left',
  pose,
  at = 0.28,
  to = 0.5,
  holding,
  still = false,
}: {
  side?: 'left' | 'right';
  pose: HandPose;
  /** Where across the scene it hangs, as a share of the width (its center). */
  at?: number;
  /** How far down it reaches, as a share of the scene's height. */
  to?: number;
  holding?: ReactNode;
  still?: boolean;
}) {
  const [tentacle, setTentacle] = useState(false);
  const [glitch, setGlitch] = useState(false);
  useEffect(() => {
    if (still) return;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const later = (ms: number, fn: () => void) => timers.push(setTimeout(fn, ms));
    // A flicker of a few frames, and the hand is the other thing.
    const swap = (to: boolean, then: () => void) => {
      setGlitch(true);
      later(140, () => setTentacle(to));
      later(300, () => {
        setGlitch(false);
        then();
      });
    };
    const surprise = (wait: number) =>
      later(wait, () =>
        swap(true, () =>
          later(1800 + Math.random() * 1600, () =>
            swap(false, () => surprise(22000 + Math.random() * 26000)),
          ),
        ),
      );
    surprise(6000 + Math.random() * 20000);
    return () => timers.forEach(clearTimeout);
  }, [still]);
  const kind: HandKind = tentacle && !still ? 'tentacle' : side === 'left' ? 'skeleton' : 'hook';
  const style = {
    '--bh-x': `${at * 100}%`,
    '--bh-to': `${to * 100}%`,
    '--tap-delay': `${-((at * 37) % 13)}s`,
    '--drip-delay': `${-((at * 53) % 4)}s`,
  } as CSSProperties;
  return (
    <div
      className={`bh bh-${pose} bh-${side} is-${kind} ${glitch ? 'is-glitch' : ''} ${still ? 'is-still' : ''}`}
      style={style}
      aria-hidden="true"
    >
      <HandShape kind={kind} />
      {holding && <div className="bh-holding">{holding}</div>}
    </div>
  );
}

// ---- Eyes ------------------------------------------------------------------------------------

/**
 * His eyes (§12.11): long, narrowed, a slit of black in an iris of embers. At Closing Time one big
 * pair opens over the scene, and the smoke behind turns into two slow whirlpools round them, lit
 * by their embers and hollow beneath (SpaceBackground.tsx finds them on the page); at
 * the table a small pair sits between the bar and his strip. They blink every 9 to 14 s, drift a
 * pixel or two toward the player's selection, and close when he loses. With reduced motion they
 * neither blink nor drift.
 */
export function BrokerEyes({
  open,
  look = 0,
  still = false,
  small = false,
}: {
  open: boolean;
  /** −1 (left) to 1 (right): where the player's selection is. */
  look?: number;
  still?: boolean;
  /** At the table. */
  small?: boolean;
}) {
  const sprite = small ? EYE_SMALL : EYE;
  const style = { '--look': still ? 0 : Math.max(-1, Math.min(1, look)) } as CSSProperties;
  const eye = (mirror: boolean) => (
    <svg
      className="be-eye"
      viewBox={`0 0 ${sprite.w} ${sprite.h}`}
      shapeRendering="crispEdges"
      style={mirror ? { transform: 'scaleX(-1)' } : undefined}
    >
      <SpritePaths sprite={sprite} />
    </svg>
  );
  return (
    <div
      className={`be ${small ? 'is-small' : ''} ${open ? 'is-open' : 'is-closed'} ${still ? 'is-still' : ''}`}
      style={style}
      aria-hidden="true"
    >
      <span className="be-lid">{eye(false)}</span>
      <span className="be-lid">{eye(true)}</span>
    </div>
  );
}

// ---- His lines -------------------------------------------------------------------------------

/** Where he speaks: the moments that may draw a line from him. */
export type LineMoment =
  | 'counter'
  | 'ringThrice'
  | 'sellPawn'
  | 'buyBack'
  | 'leaveEmpty'
  | 'rare'
  | 'backRoom'
  | 'firstCounter'
  | 'closing'
  | 'runLost'
  | 'closingLost';

/** His lines (§8.5): short, dry, courteous, a little possessive; objects and prices, never himself. */
export const LINES: Readonly<Record<LineMoment, readonly MessageKey[]>> = {
  counter: ['broker.line.counter1', 'broker.line.counter2', 'broker.line.counter3'],
  ringThrice: ['broker.line.ringThrice'],
  sellPawn: ['broker.line.sellPawn'],
  buyBack: ['broker.line.buyBack'],
  leaveEmpty: ['broker.line.leaveEmpty'],
  rare: ['broker.line.rare'],
  backRoom: ['broker.line.backRoom'],
  firstCounter: ['broker.line.firstCounter'],
  closing: ['broker.line.closing'],
  runLost: ['broker.line.runLost'],
  closingLost: ['broker.line.closingLost'],
};

const RARITY_KEY = 'pawned.broker.v1';

/**
 * Whether he speaks at this visit to the Counter (§18.7): rarely, a line every third visit and
 * never two visits in a row. Moments that matter (Closing Time, the end of a run, a first
 * Counter) always have their line.
 */
export function brokerSpeaks(moment: LineMoment): boolean {
  if (moment !== 'counter') return true;
  let n: number;
  try {
    n = Number(localStorage.getItem(RARITY_KEY) ?? '0') || 0;
    localStorage.setItem(RARITY_KEY, String(n + 1));
  } catch {
    // Without storage he keeps his own count for the session.
    n = sessionCount++;
  }
  return n % 3 === 2;
}
let sessionCount = 0;

/** A line of his, chosen for the moment (the same each time for a given seed). */
export function lineFor(moment: LineMoment, seed = 0): string {
  const list = LINES[moment];
  return t(list[Math.abs(seed) % list.length]!);
}

/**
 * One of his lines (§8.4): after a 400 ms hush it writes itself at 28 ms a letter, stays for 1.8 s
 * plus 60 ms a word, and leaves on a glide. While he speaks the smoke's vortex tightens by 15%,
 * and a low breath sounds under it. A tap dismisses it.
 */
export function BrokerLine({
  text,
  still = false,
  inline = false,
  onDone,
}: {
  text: string;
  still?: boolean;
  /** In a place of its own in the scene (the Counter's tray), not over the scene's upper third. */
  inline?: boolean;
  onDone: () => void;
}) {
  const [shown, setShown] = useState(still ? text.length : 0);
  const [leaving, setLeaving] = useState(false);
  const done = useEffectEvent(onDone);
  useEffect(() => {
    const twist = getScene().twist;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const at = (ms: number, fn: () => void) => timers.push(setTimeout(fn, ms));
    const words = text.split(/\s+/).length;
    const write = still ? 0 : text.length * 28;
    at(400, () => {
      patchScene({ twist: twist * 1.15 });
      sounds.play('breath');
      if (!still)
        for (let i = 1; i <= text.length; i++) {
          at(i * 28, () => setShown(i));
          if (i % 9 === 0) at(i * 28, () => sounds.play('nib'));
        }
    });
    const stay = 400 + write + 1800 + words * 60;
    at(stay, () => {
      setLeaving(true);
      patchScene({ twist });
    });
    at(stay + 900, done);
    return () => {
      timers.forEach(clearTimeout);
      patchScene({ twist });
    };
  }, [text, still]);
  return (
    <p
      className={`bl ${inline ? 'is-inline' : ''} ${leaving ? 'is-leaving' : ''}`}
      role="status"
      onClick={() => {
        setLeaving(true);
        setTimeout(onDone, still ? 0 : 400);
      }}
    >
      <span aria-hidden="true">{text.slice(0, shown)}</span>
      <span className="bl-rest" aria-hidden="true">
        {text.slice(shown)}
      </span>
      <span className="sr-only">{text}</span>
    </p>
  );
}

// ---- The candle ------------------------------------------------------------------------------

/**
 * The run's candle (§12.12): tall at Night I, half burnt at Night II, a stub at Night III,
 * guttering at Closing Time, out in After Hours (a thread of smoke).
 */
/** A rectangle of pixels, as path data. */
const px = (x: number, y: number, w = 1, h = 1) => `M${x} ${y}h${w}v${h}h${-w}z`;

export function Candle({ night, size = 28 }: { night: number; size?: number }) {
  const height = night <= 1 ? 1 : night === 2 ? 0.62 : night === 3 ? 0.34 : 0.18;
  const out = night > 4;
  // Pixel art, 7 × 28: the wax stands up to 16 pixels tall on its brass dish.
  const top = Math.round(25 - height * 16);
  const theme = useTheme();
  // In the sea it is a stick of incense burning down (§13).
  if (theme === 'sea')
    return (
      <span
        className={`candle is-incense ${out ? 'is-out' : ''}`}
        style={{ '--h': size } as CSSProperties}
        aria-hidden="true"
      >
        <svg viewBox="0 0 7 28" width={size / 4} height={size} shapeRendering="crispEdges">
          <path fill="#8a5a3a" d={px(3, top, 1, 26 - top)} />
          <path fill="#c9973a" d={px(1, 26, 5, 2)} />
          {!out && <path className="candle-flame" fill="#ff8a4a" d={px(3, top - 1)} />}
          <path
            className="candle-smoke"
            fill="rgba(200,200,210,0.55)"
            d={px(2, top - 4) + px(3, top - 7) + px(4, top - 10) + px(3, top - 13)}
          />
        </svg>
      </span>
    );
  return (
    <span
      className={`candle ${night === 4 ? 'is-guttering' : ''} ${out ? 'is-out' : ''}`}
      style={{ '--h': size } as CSSProperties}
      aria-hidden="true"
    >
      <svg viewBox="0 0 7 28" width={size / 4} height={size} shapeRendering="crispEdges">
        {/* The wax, lit on its left, a drip down its side; the brass dish under it. */}
        <path fill="#e9e2d0" d={px(2, top, 3, 26 - top)} />
        <path fill="#fff8ea" d={px(2, top, 1, 26 - top) + px(2, top, 3, 1)} />
        <path fill="#b8ae98" d={px(4, top + 1, 1, 25 - top)} />
        <path fill="#e9e2d0" d={px(1, top + 1, 1, 3)} />
        <path fill="#fff0b8" d={px(0, 26, 7, 1)} />
        <path fill="#c4a062" d={px(1, 27, 5, 1)} />
        <path fill="#2a1d12" d={px(3, top - 1)} />
        {!out && (
          <g className="candle-flame">
            <path fill="#ff9a3a" d={px(2, top - 3, 3, 1) + px(3, top - 2)} />
            <path fill="#ffcf8a" d={px(3, top - 6, 1, 2) + px(2, top - 4, 3, 1)} />
            <path fill="#fff8e4" d={px(3, top - 4, 1, 2)} />
          </g>
        )}
        {out && (
          <path
            className="candle-smoke"
            fill="rgba(154,150,163,0.6)"
            d={px(3, top - 3) + px(2, top - 6) + px(3, top - 9) + px(4, top - 12) + px(3, top - 15)}
          />
        )}
      </svg>
    </span>
  );
}

// ---- The House Rule's placard and the props --------------------------------------------------

/**
 * The House Rule's placard (§11.6), at the top edge of the board: the regular's emblem (or the
 * patron's prop) and the rules' names; a tap shows the rules. Each name fires in the Tally.
 */
export function Placard({
  rules,
  emblem,
  clock,
  onOpen,
}: {
  rules: readonly HouseRuleSpec[];
  /** The regular's or patron's emblem key. */
  emblem?: string;
  /** What a rule that counts turns has left (night/ruleClock.ts), shown beside its name. */
  clock?: (r: HouseRuleSpec) => RuleClock | null;
  onOpen: () => void;
}) {
  if (rules.length === 0) return null;
  return (
    <button type="button" className="placard" onClick={onOpen} title={t('placard.open')}>
      {emblem && <Emblem name={emblem} size={22} className="placard-prop" />}
      <span className="placard-names">
        {rules.map((r, i) => {
          const c = clock?.(r);
          return (
            <span key={`${r.id}-${i}`} data-tally-rule={r.id}>
              {itemName(`rule.${r.id}`)}
              {c && (
                <b className={`placard-clock ${c.urgent ? 'is-urgent' : ''}`} title={c.label}>
                  {c.hourglass && (
                    <svg
                      viewBox="0 0 5 7"
                      width="7"
                      height="10"
                      aria-hidden="true"
                      shapeRendering="crispEdges"
                    >
                      <path
                        d="M0 0h5v1h-1v2h-1v1h1v2h1v1h-5v-1h1v-2h1v-1h-1v-2h-1z"
                        fill="currentColor"
                      />
                    </svg>
                  )}
                  <span aria-hidden="true">{c.text}</span>
                  <span className="sr-only">{c.label}</span>
                </b>
              )}
            </span>
          );
        })}
      </span>
    </button>
  );
}

/** A patron's or regular's one prop, in silhouette with a faint rim (§12.11). */
export function Prop({ name, size = 30 }: { name: string; size?: number }) {
  return <Emblem name={name} size={size} tone="silhouette" className="prop" />;
}
